/*
Panel pod boiskiem: wynik akcji i formularz odbioru vouchera.

Logika z prototypu: poniżej progu - ile brakuje; ponad progiem - formularz; ponad progiem,
gdy z tej przeglądarki voucher już poszedł - adres, na który go wysłaliśmy. Kodu nie
pokazujemy nigdzie, backend wysyła go wyłącznie mailem (patrz claim.js).

Pula: front nie zna stanu puli, dopóki nie spróbuje wydać kodu. Gdy backend odpowie
pool_exhausted, zapamiętujemy to (POOL_KEY) i kolejne wygrane w tej przeglądarce kończą
się komunikatem zamiast formularza - grać można dalej, tylko bez vouchera.

Walidacja: imię i nazwisko tylko wymagane (do 100 znaków pilnuje maxlength, tak jak
backend), e-mail do 255 znaków, telefon komórkowy z kierunkowym (phone.js), zgody tak,
jak oznaczy je Twig (`required`). To, czego front nie sprawdzi (DNS domeny, skrzynki tymczasowe), wraca
z backendu jako błąd pola.

Na czas wysyłki zasłania stronę pełnoekranowy loader z checkoutu (loader.js).

Payload to dokładnie to, co przyjmuje CouponCampaignClaimRequest: name, email, phone,
newsletter (= zgoda marketingowa). Akceptacji regulaminu backend nie zapisuje - bez niej
front po prostu nie wysyła formularza.
*/

import { ClaimError, claimVoucher } from './claim';
import { loadLoader } from './loader';
import { initPhoneInput, loadPhoneLib, readPhone } from './phone';
import {
    CLAIM_KEY,
    POOL_KEY,
    POOL_TTL,
    fillText,
    pluralForm,
    readStorage,
    writeStorage,
} from './utils';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMAIL_MAX = 255;
const CONSENT_KEYS = ['rules', 'marketing'];
const FIELD_KEYS = ['name', 'email', 'phone', ...CONSENT_KEYS];

// Pole formularza -> klucz błędu z backendu (regulamin backend nie dostaje).
const SERVER_FIELDS = { name: 'name', email: 'email', phone: 'phone', marketing: 'newsletter' };

const normalizeName = (value) => value.trim().replace(/\s+/g, ' ');

export function isPoolExhausted() {
    const at = Number(readStorage(POOL_KEY));

    return Boolean(at) && Date.now() - at < POOL_TTL;
}

export function createResult({ el, config, onPoolExhausted }) {
    const { texts, threshold } = config;

    const claimErrorTexts = {
        already_claimed: texts.form_error_already_claimed,
        pool_exhausted: texts.form_error_pool_exhausted,
        closed: texts.form_error_closed,
        throttled: texts.form_error_throttled,
    };

    const emptyTexts = {
        name: texts.form_error_name_empty,
        email: texts.form_error_email_empty,
        phone: texts.form_error_phone_empty,
        rules: texts.form_error_rules,
        marketing: texts.form_error_marketing,
    };

    // Błędy pól z backendu - komunikaty piszemy sami, backendowe bywają surowymi kluczami.
    const serverFieldTexts = {
        name: texts.form_error_name_empty,
        email: texts.form_error_email_rejected,
        phone: texts.form_error_phone,
        marketing: texts.form_error_marketing,
    };

    function setFieldError(key, message) {
        const field = el.fields[key];

        field.error.textContent = message ?? '';
        field.wrap.classList.toggle('is-invalid', Boolean(message));
        field.input.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    // Komunikat błędu pola albo null, gdy pole jest poprawne.
    async function checkField(key) {
        const { input } = el.fields[key];

        if (CONSENT_KEYS.includes(key)) {
            return input.required && !input.checked ? emptyTexts[key] : null;
        }

        const value = input.value.trim();

        if (!value) {
            return emptyTexts[key];
        }

        if (key === 'name') {
            return null;
        }

        if (key === 'email') {
            const valid = value.length <= EMAIL_MAX && EMAIL_PATTERN.test(value);

            return valid ? null : texts.form_error_email;
        }

        return (await readPhone(input)) ? null : texts.form_error_phone;
    }

    async function validateForm() {
        const messages = await Promise.all(FIELD_KEYS.map(checkField));

        FIELD_KEYS.forEach((key, i) => setFieldError(key, messages[i]));

        return FIELD_KEYS.find((key, i) => messages[i]) ?? null;
    }

    function showPoolExhausted() {
        el.title.textContent = texts.pool_title;
        el.text.textContent = texts.pool_text;
        el.form.hidden = true;
        el.sent.hidden = true;
    }

    function showSentTo(email) {
        el.sentEmail.textContent = email;
        el.sent.hidden = false;
        el.form.hidden = true;
    }

    function show(points) {
        const claimedEmail = readStorage(CLAIM_KEY);
        const passed = points >= threshold;

        el.label.textContent = fillText(texts.result_label, { points });
        el.root.hidden = false;
        el.form.hidden = true;
        el.sent.hidden = true;

        if (passed && claimedEmail) {
            el.title.textContent = texts.result_again_title;
            el.text.textContent = texts.result_again_text;
            showSentTo(claimedEmail);
        } else if (passed && isPoolExhausted()) {
            showPoolExhausted();
        } else if (passed) {
            el.title.textContent = fillText(texts.result_won_title, {
                points,
                points_word: pluralForm(
                    points,
                    texts.points_one,
                    texts.points_few,
                    texts.points_many
                ),
            });
            el.text.textContent = texts.result_won_text;
            el.form.hidden = false;
            initPhoneInput(el.fields.phone.input).catch(() => {});
            loadPhoneLib().catch(() => {});
            loadLoader(texts);
        } else {
            const missing = threshold - points;
            const title = missing === 1 ? texts.result_missing_one : texts.result_missing_many;

            el.title.textContent = fillText(title, { missing });
            // "Jeszcze jeden rzut zza linii" jest prawdą tylko, gdy brakuje najwyżej 3 punktów.
            if (points === 0) {
                el.text.textContent = texts.result_zero_text;
            } else {
                el.text.textContent =
                    missing <= 3 ? texts.result_close_text : texts.result_far_text;
            }
        }

        el.root.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function hide() {
        el.root.hidden = true;
    }

    FIELD_KEYS.forEach((key) => {
        const { input } = el.fields[key];
        const isConsent = CONSENT_KEYS.includes(key);

        // Poprawiane pole traci błąd od razu, a wypełnione sprawdzamy przy wyjściu z niego.
        input.addEventListener(isConsent ? 'change' : 'input', () => {
            if (!isConsent || input.checked) {
                setFieldError(key, null);
            }
        });

        if (!isConsent) {
            input.addEventListener('blur', async () => {
                if (input.value.trim()) {
                    setFieldError(key, await checkField(key));
                }
            });
        }
    });

    el.form.addEventListener('submit', async (e) => {
        e.preventDefault();

        if (el.submit.disabled) {
            return;
        }

        el.error.textContent = '';
        el.submit.disabled = true;

        const submitLabel = el.submit.textContent;
        const firstInvalid = await validateForm();

        if (firstInvalid) {
            el.submit.disabled = false;
            el.fields[firstInvalid].input.focus();

            return;
        }

        const name = normalizeName(el.fields.name.input.value);
        const email = el.fields.email.input.value.trim();

        el.submit.textContent = texts.form_sending;

        const overlay = await loadLoader(texts);

        overlay?.show();

        try {
            await claimVoucher(config.claim_url, {
                name,
                email,
                phone: await readPhone(el.fields.phone.input),
                newsletter: el.fields.marketing.input.checked,
            });

            writeStorage(CLAIM_KEY, email);
            el.title.textContent = fillText(texts.claimed_title, { name: name.split(' ')[0] });
            el.text.textContent = texts.claimed_text;
            showSentTo(email);
            el.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (err) {
            const fields = err instanceof ClaimError && err.type === 'validation' ? err.fields : {};
            const invalid = FIELD_KEYS.filter((key) => fields[SERVER_FIELDS[key]]);

            invalid.forEach((key) => setFieldError(key, serverFieldTexts[key]));

            if (invalid.length) {
                el.fields[invalid[0]].input.focus();
            } else if (err.type === 'pool_exhausted') {
                writeStorage(POOL_KEY, String(Date.now()));
                showPoolExhausted();
                onPoolExhausted?.();
            } else {
                el.error.textContent = claimErrorTexts[err.type] ?? texts.form_error_generic;
            }
        } finally {
            overlay?.hide();
            el.submit.disabled = false;
            el.submit.textContent = submitLabel;
        }
    });

    return { show, hide };
}
